import { useCallback } from 'react';
import { usePostHog } from 'posthog-js/react';
import { getStoredUTMParams } from '../utils/utm';
import { isLikelyAutomatedClient } from '../utils/isLikelyAutomatedClient';

declare global {
  interface Window {
    analytics: any;
  }
}

const mergeUTMs = (data: Record<string, any>) => {
  return {
    ...data,
    ...getStoredUTMParams(),
  };
};

export const useTracking = () => {
  const posthog = usePostHog();
  const isAutomatedClient = isLikelyAutomatedClient();

  const identify = useCallback(
    (userId, userData) => {
      if (isAutomatedClient || !window.analytics) {
        return;
      }
      const enrichedData = mergeUTMs(userData);

      if (userId) {
        window.analytics.identify(userId, enrichedData);
        posthog?.identify(userId, enrichedData);
        return;
      }
      window.analytics.identify(enrichedData);
    },
    [isAutomatedClient, posthog]
  );

  const page = useCallback(
    (page, additionalParams?: any) => {
      if (isAutomatedClient || !window.analytics) {
        return;
      }
      const utms = getStoredUTMParams();
      window.analytics.page(page, { ...utms, ...additionalParams });

      // We use capture with the page name instead of
      // https://posthog.com/docs/libraries/js/features#manually-capturing-pageviews-and-pageleaves-in-single-page-apps
      // as they describe because in different places,
      // we use the same URL even when changing page content.
      posthog?.capture(page, {
        $current_url: window.location.href,
        ...utms,
        ...additionalParams,
      });
    },
    [isAutomatedClient, posthog]
  );

  const track = useCallback(
    (eventName, eventData) => {
      if (isAutomatedClient || !window.analytics) {
        return;
      }
      const enrichedData = mergeUTMs(eventData);

      window.analytics.track(eventName, enrichedData);
      posthog?.capture(eventName, {
        ...enrichedData,
        $current_url: window.location.href,
      });
    },
    [isAutomatedClient, posthog]
  );

  return {
    identify,
    page,
    track,
  };
};
